// /apps/patient/src/pages/records/VisitsTab.tsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientApiService } from '../../services/api';
import {
  Stethoscope, Clock, ChevronRight,
  AlertCircle, FileText, Activity, User
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

interface Encounter {
  id: string;
  created_at: number;
  encounter_date: string;
  encounter_type: string;
  visit_type?: string;
  chief_complaint?: string;
  reason_for_visit?: string;
  status: string;
  provider_name?: string;
  location_name?: string;
  clinical_note_id?: string;
  documentation_status?: string;
  duration_minutes?: number;
  vitals?: any;
}

interface PaginationInfo {
  itemsTotal: number;
  pageTotal: number;
  curPage: number;
  perPage: number;
}

export function VisitsTab() {
  const navigate = useNavigate();
  const [encounters, setEncounters] = useState<Encounter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    itemsTotal: 0,
    pageTotal: 1,
    curPage: 1,
    perPage: 20
  });
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    fetchEncounters();
  }, []);

  const fetchEncounters = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = { page: 1, perPage: 20 };

      const response = await patientApiService.getMyEncounters(params);

      const encountersData = response.items || [];
      setEncounters(encountersData);

      setPagination({
        itemsTotal: response.itemsTotal || 0,
        pageTotal: response.pageTotal || 1,
        curPage: response.curPage || 1,
        perPage: response.perPage || 20
      });
    } catch (err: any) {
      console.error('Error fetching encounters:', err);
      setError(err.message || 'Failed to load encounters');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreEncounters = async () => {
    if (pagination.curPage >= pagination.pageTotal) return;

    setLoadingMore(true);
    try {
      const nextPage = pagination.curPage + 1;
      const params: any = {
        page: nextPage,
        perPage: pagination.perPage
      };

      const response = await patientApiService.getMyEncounters(params);

      const newEncounters = response.items || [];
      setEncounters([...encounters, ...newEncounters]);

      setPagination({
        ...pagination,
        curPage: response.curPage || nextPage
      });
    } catch (err) {
      console.error('Error loading more encounters:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const formatEncounterType = (type: string) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getEncounterIcon = (type: string) => {
    switch (type) {
      case 'initial_evaluation':
        return <FileText className="h-5 w-5 text-blue-500" />;
      case 'follow_up':
        return <Activity className="h-5 w-5 text-green-500" />;
      case 'telehealth':
        return <Stethoscope className="h-5 w-5 text-purple-500" />;
      default:
        return <Stethoscope className="h-5 w-5 text-primary" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'in_progress':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'scheduled':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'cancelled':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Group encounters by date
  const groupedEncounters = encounters.reduce((groups: Record<string, Encounter[]>, encounter) => {
    const date = new Date(encounter.encounter_date).toLocaleDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(encounter);
    return groups;
  }, {});

  const sortedDates = Object.keys(groupedEncounters).sort((a, b) =>
    new Date(b).getTime() - new Date(a).getTime()
  );

  if (loading) {
    return (
      <div className="mt-6">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
        <p className="text-red-700">{error}</p>
        <button
          onClick={fetchEncounters}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Encounters List */}
      {encounters.length === 0 ? (
        <div className="mt-8 text-center py-12 bg-white rounded-lg">
          <Stethoscope className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No visits found</p>
          <p className="text-sm text-gray-400 mt-1">Your visit records will appear here</p>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {sortedDates.map(date => (
            <div key={date}>
              <h3 className="text-sm font-medium text-gray-500 mb-3">
                {format(new Date(date), 'EEEE, MMMM d, yyyy')}
              </h3>
              <div className="space-y-4">
                {groupedEncounters[date].map((encounter) => (
                  <div
                    key={encounter.id}
                    onClick={() => navigate(`/records/${encounter.id}`)}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="p-2 bg-gray-50 rounded-lg">
                          {getEncounterIcon(encounter.encounter_type)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-medium text-gray-900">
                              {formatEncounterType(encounter.encounter_type)}
                            </h3>
                            {(encounter.clinical_note_id || encounter.documentation_status === 'signed') && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                                <FileText className="h-3 w-3" />
                                Note Available
                              </span>
                            )}
                          </div>

                          {(encounter.chief_complaint || encounter.reason_for_visit) && (
                            <p className="text-sm text-gray-600 mb-2">
                              {encounter.chief_complaint || encounter.reason_for_visit}
                            </p>
                          )}

                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {format(new Date(encounter.encounter_date), 'h:mm a')}
                            </span>

                            {encounter.provider_name && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {encounter.provider_name}
                              </span>
                            )}

                            {encounter.duration_minutes && (
                              <span>{encounter.duration_minutes} min</span>
                            )}

                            <span className={cn(
                              "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border",
                              getStatusColor(encounter.status)
                            )}>
                              {encounter.status.charAt(0).toUpperCase() + encounter.status.slice(1)}
                            </span>
                          </div>

                          {/* Quick Vitals Preview */}
                          {encounter.vitals && Object.keys(encounter.vitals).length > 0 && (
                            <div className="mt-3 pt-3 border-t flex gap-4 text-xs text-gray-600">
                              {encounter.vitals.blood_pressure_systolic && (
                                <span>
                                  BP: {encounter.vitals.blood_pressure_systolic}/{encounter.vitals.blood_pressure_diastolic}
                                </span>
                              )}
                              {encounter.vitals.heart_rate && (
                                <span>HR: {encounter.vitals.heart_rate}</span>
                              )}
                              {encounter.vitals.temperature && (
                                <span>Temp: {encounter.vitals.temperature}°F</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Load More Button */}
          {pagination.curPage < pagination.pageTotal && (
            <div className="text-center pt-4">
              <button
                onClick={loadMoreEncounters}
                disabled={loadingMore}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
